import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Loader2 } from "lucide-react";
import { format } from "date-fns";

const FALLBACK_MINISTRY_LABEL = "General Ministry";

type BulletinItem = {
  id: string;
  label: string;
  person: string;
  time: string;
  type: "item" | "header" | "divider";
};

type BulletinTemplate = {
  id: string;
  name: string;
  service_type: string;
  service_date: string | null;
  bulletin_items: BulletinItem[];
};

type ParticipantCredit = {
  role: string;
  serviceDate: string;
  serviceType: string;
  bulletinId: string;
};

type ParticipantMemberRecord = {
  id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
};

const splitPeople = (value: string) =>
  value
    .split(/,|&|\band\b|\//gi)
    .map((part) => part.trim())
    .filter(Boolean);

const getInitials = (fullName: string) =>
  fullName
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .join("")
    .slice(0, 2) || "--";

const avatarToneForName = (fullName: string) => {
  return "bg-primary/10 text-primary";
};

export default function BulletinParticipantDetailPage() {
  const navigate = useNavigate();
  const { profile } = useChurch();
  const { participantName } = useParams<{ participantName: string }>();
  const decodedName = decodeURIComponent(participantName || "").trim();
  const today = format(new Date(), "yyyy-MM-dd");

  const { data: pastBulletins = [], isLoading } = useQuery({
    queryKey: ["bulletin_participant_detail", profile?.id, decodedName],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bulletin_templates")
        .select("id, name, service_type, service_date, bulletin_items")
        .eq("church_id", profile!.id)
        .eq("is_template", false)
        .lt("service_date", today)
        .order("service_date", { ascending: false });
      if (error) throw error;

      return ((data || []) as any[]).map((row) => ({
        ...row,
        bulletin_items: (Array.isArray(row.bulletin_items) ? row.bulletin_items : []) as BulletinItem[],
      })) as BulletinTemplate[];
    },
    enabled: !!profile?.id && !!decodedName,
  });

  const { data: participantMember, isLoading: memberLoading } = useQuery({
    queryKey: ["participant_member_lookup", profile?.id, decodedName],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name, phone, email")
        .eq("church_id", profile!.id)
        .ilike("full_name", decodedName)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data || null) as ParticipantMemberRecord | null;
    },
    enabled: !!profile?.id && !!decodedName,
  });

  const credits = useMemo(() => {
    const list: ParticipantCredit[] = [];
    const target = decodedName.toLowerCase();

    pastBulletins.forEach((bulletin) => {
      (bulletin.bulletin_items || []).forEach((item) => {
        if (item.type !== "item") return;
        if (!item.person?.trim() || !item.label?.trim()) return;

        splitPeople(item.person).forEach((name) => {
          if (name.toLowerCase() !== target) return;
          list.push({
            role: item.label.trim(),
            serviceDate: bulletin.service_date || "",
            serviceType: bulletin.service_type || FALLBACK_MINISTRY_LABEL,
            bulletinId: bulletin.id,
          });
        });
      });
    });

    return list.sort((a, b) => b.serviceDate.localeCompare(a.serviceDate));
  }, [pastBulletins, decodedName]);

  const uniqueRoles = useMemo(
    () => Array.from(new Set(credits.map((credit) => credit.role))).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
    [credits],
  );

  const uniqueBulletins = useMemo(
    () => new Set(credits.map((credit) => credit.bulletinId)).size,
    [credits],
  );

  if (!decodedName) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate("/communication?tab=participants")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <p className="text-sm text-muted-foreground">No participant selected.</p>
      </div>
    );
  }

  if (isLoading || memberLoading) {
    return (
      <div className="min-h-[360px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (credits.length === 0) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate("/communication?tab=participants")}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <div className="stat-card text-center py-12 text-muted-foreground text-sm">
          No past participation history found for {decodedName}.
        </div>
      </div>
    );
  }

  const participantUid = participantMember?.id ? `M-${participantMember.id.slice(0, 8).toUpperCase()}` : "Not linked";
  const participantPhone = participantMember?.phone || "Not provided";
  const participantEmail = participantMember?.email || "Not provided";

  return (
    <div className="space-y-4">
      <Button variant="outline" onClick={() => navigate("/communication?tab=participants")}>
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Participants
      </Button>

      <div className="stat-card">
        <div className="flex items-center gap-3">
          <div className={`h-12 w-12 rounded-full flex items-center justify-center text-base font-semibold ${avatarToneForName(decodedName)}`}>
            {getInitials(decodedName)}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-heading font-semibold text-foreground">{decodedName}</h2>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
              <p className="text-muted-foreground truncate"><span className="font-medium text-foreground">UID:</span> {participantUid}</p>
              <p className="text-muted-foreground truncate"><span className="font-medium text-foreground">Phone:</span> {participantPhone}</p>
              <p className="text-muted-foreground truncate"><span className="font-medium text-foreground">Email:</span> {participantEmail}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="rounded-xl shadow-none border-border/70">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Services</p>
            <p className="mt-1 text-3xl font-semibold text-foreground">{credits.length}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl shadow-none border-border/70">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Unique Roles</p>
            <p className="mt-1 text-3xl font-semibold text-foreground">{uniqueRoles.length}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl shadow-none border-border/70">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Bulletins</p>
            <p className="mt-1 text-3xl font-semibold text-foreground">{uniqueBulletins}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-xl shadow-none border-border/70">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Involvement History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {credits.map((entry, index) => (
              <div key={`${entry.bulletinId}-${entry.role}-${entry.serviceDate}-${index}`} className="px-4 py-3 flex flex-wrap items-center gap-3">
                <p className="w-[110px] text-sm font-semibold text-foreground">
                  {entry.serviceDate ? format(new Date(`${entry.serviceDate}T00:00:00`), "MMM d, yyyy") : "No date"}
                </p>
                <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-800 border-emerald-200">{entry.role}</Badge>
                <p className="text-sm text-muted-foreground truncate min-w-0">{entry.serviceType}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}